/**
 * Comprehensive Array Validation Utilities
 * Prevents TypeError: X.map is not a function and similar array operation errors
 */

/**
 * Safely map over an array - returns empty array if input is not an array
 */
export function safeMap<T, U>(
  array: T[] | undefined | null,
  mapper: (item: T, index: number, array: T[]) => U,
  defaultValue: U[] = []
): U[] {
  if (!Array.isArray(array)) {
    console.warn('safeMap: Expected array but got:', typeof array, array);
    return defaultValue;
  }
  try {
    return array.map(mapper);
  } catch (error) {
    console.error('safeMap: Error during mapping:', error);
    return defaultValue;
  }
}

/**
 * Safely filter an array - returns empty array if input is not an array
 */
export function safeFilter<T>(
  array: T[] | undefined | null,
  predicate: (item: T, index: number, array: T[]) => boolean,
  defaultValue: T[] = []
): T[] {
  if (!Array.isArray(array)) {
    console.warn('safeFilter: Expected array but got:', typeof array, array);
    return defaultValue;
  }
  try {
    return array.filter(predicate);
  } catch (error) {
    console.error('safeFilter: Error during filtering:', error);
    return defaultValue;
  }
}

/**
 * Safely reduce an array - returns initialValue if input is not an array
 */
export function safeReduce<T, U>(
  array: T[] | undefined | null,
  reducer: (accumulator: U, currentValue: T, index: number, array: T[]) => U,
  initialValue: U
): U {
  if (!Array.isArray(array)) {
    console.warn('safeReduce: Expected array but got:', typeof array, array);
    return initialValue;
  }
  try {
    return array.reduce(reducer, initialValue);
  } catch (error) {
    console.error('safeReduce: Error during reduction:', error);
    return initialValue;
  }
}

/**
 * Safely find an item in an array - returns undefined if input is not an array
 */
export function safeFind<T>(
  array: T[] | undefined | null,
  predicate: (item: T, index: number, array: T[]) => boolean
): T | undefined {
  if (!Array.isArray(array)) {
    console.warn('safeFind: Expected array but got:', typeof array, array);
    return undefined;
  }
  try {
    return array.find(predicate);
  } catch (error) {
    console.error('safeFind: Error during find:', error);
    return undefined;
  }
}

/**
 * Safely get array length - returns 0 if input is not an array
 */
export function safeLength(array: any[] | undefined | null): number {
  if (!Array.isArray(array)) {
    return 0;
  }
  return array.length;
}

/**
 * Safely access array by index - returns defaultValue if out of bounds or not an array
 */
export function safeGetAtIndex<T>(
  array: T[] | undefined | null,
  index: number,
  defaultValue: T | undefined = undefined
): T | undefined {
  if (!Array.isArray(array) || index < 0 || index >= array.length) {
    return defaultValue;
  }
  return array[index];
}

/**
 * Ensures value is an array - wraps single values, returns empty array for null/undefined
 */
export function ensureArray<T>(value: T | T[] | undefined | null): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === null || value === undefined) {
    return [];
  }
  return [value];
}

/**
 * Generic safe array operations class
 */
export class SafeArray<T> {
  constructor(private data: T[] | undefined | null) {}

  get array(): T[] {
    return ensureArray(this.data);
  }

  map<U>(mapper: (item: T, index: number, array: T[]) => U): U[] {
    return safeMap(this.data, mapper);
  }

  filter(predicate: (item: T, index: number, array: T[]) => boolean): T[] {
    return safeFilter(this.data, predicate);
  }

  reduce<U>(reducer: (acc: U, item: T, index: number, array: T[]) => U, initialValue: U): U {
    return safeReduce(this.data, reducer, initialValue);
  }

  find(predicate: (item: T, index: number, array: T[]) => boolean): T | undefined {
    return safeFind(this.data, predicate);
  }

  get length(): number {
    return safeLength(this.data);
  }

  isEmpty(): boolean {
    return this.length === 0;
  }

  isNotEmpty(): boolean {
    return this.length > 0;
  }
}

/**
 * Create a SafeArray instance
 */
export function createSafeArray<T>(data: T[] | undefined | null): SafeArray<T> {
  return new SafeArray(data);
}

/**
 * React hook for safe array operations
 */
export function useSafeArray<T>(data: T[] | undefined | null): SafeArray<T> {
  return createSafeArray(data);
}